import { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, EmailAccount, EmailAlias, EmailInfo, MailHealth } from '../../api';
import { Alert, Loading, useAction, useLoad } from '../../Components/ui';
import type { TabProps } from './ControlPanel';

const QUOTAS = [{ mb: 1000, label: '1 GB' }, { mb: 3000, label: '3 GB' }, { mb: 5000, label: '5 GB' }, { mb: 10000, label: '10 GB' }];

const CHECK_LABEL: Record<MailHealth['checks'][number]['id'], string> = {
    mx: 'MX — delivers incoming mail to us',
    spf: 'SPF — allows our servers to send for the domain',
    dkim: 'DKIM — signs outgoing mail',
    dmarc: 'DMARC — protects against spoofing',
};

function generatePassword(): string {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!#%+?';
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, b => chars[b % chars.length]).join('');
}

/** Shows a password once, after creating a mailbox or changing its password. */
function PasswordBox({ email, password, title, onDone }: { email: string; password: string; title: string; onDone: () => void }) {
    return (
        <div className="credentials-box" style={{ marginBottom: '1rem' }}>
            <p>{title} Copy the password now — it will not be shown again.</p>
            <div className="cred-row"><span>Email</span><span className="mono">{email}</span></div>
            <div className="cred-row">
                <span>Password</span><span className="mono">{password}</span>
                <button className="link-button" onClick={() => navigator.clipboard?.writeText(password)}>Copy</button>
            </div>
            <div className="btn-row" style={{ marginTop: '0.75rem' }}><button className="btn-outline small" onClick={onDone}>Done</button></div>
        </div>
    );
}

function HealthCard({ health, domain, linkedDomain }: { health: MailHealth; domain: string | null; linkedDomain: TabProps['detail']['linkedDomain'] }) {
    const failing = health.checks.filter(c => !c.ok);
    return (
        <div className="panel-card">
            <div className="panel-card-header">
                <h3>Email health</h3>
                <span className={`tag ${health.secured ? 'tag-completed' : 'tag-progress'}`}>{health.secured ? 'Secured' : 'Needs attention'}</span>
            </div>
            {health.secured ? (
                <p className="card-text">MX, SPF, DKIM and DMARC are in place, so mail reaches you and isn't flagged as spam.</p>
            ) : (
                <>
                    <p className="card-text" style={{ marginBottom: '0.75rem' }}>
                        These records are set up automatically with the first mailbox.{' '}
                        {linkedDomain
                            ? <>Fix the ones below in the DNS of <Link className="text-link" to={`/panel/${linkedDomain.id}`}>{linkedDomain.domain}</Link>.</>
                            : <>Add or fix the ones below where the DNS for {domain} is managed: its domain or DNS package with us, or your DNS provider.</>}
                    </p>
                    <div className="table-wrap">
                        <table className="data-table">
                            <thead><tr><th>Record</th><th>Now</th><th>Should be</th></tr></thead>
                            <tbody>
                                {failing.map(c => (
                                    <tr key={c.id}>
                                        <td>{CHECK_LABEL[c.id]}</td>
                                        <td className="mono">{c.found ?? 'missing'}</td>
                                        <td className="mono">{c.expected}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
}

function MailboxRow({ account, base, busy, run, reload, onPassword }: {
    account: EmailAccount; base: string; busy: boolean;
    run: (fn: () => Promise<void>) => Promise<void>; reload: () => Promise<void>;
    onPassword: (email: string, password: string) => void;
}) {
    const [editing, setEditing] = useState(false);
    const [password, setPassword] = useState('');
    const [quotaMb, setQuotaMb] = useState(account.quotaMb ?? 3000);
    const url = `${base}/email/accounts/${encodeURIComponent(account.email)}`;

    const save = (e: FormEvent) => {
        e.preventDefault();
        void run(async () => {
            await api.patch(url, { ...(password ? { password } : {}), ...(quotaMb !== account.quotaMb ? { quotaMb } : {}) });
            if (password) onPassword(account.email, password);
            setEditing(false);
            setPassword('');
            await reload();
        });
    };
    const setActive = (active: boolean) => {
        if (!active && !window.confirm(`Pause ${account.email}? It can't log in or receive mail until resumed. Existing mail is kept.`)) return;
        void run(async () => { await api.patch(url, { active }); await reload(); });
    };
    const remove = () => {
        if (!window.confirm(`Delete ${account.email} and all its email? Aliases forwarding to it are removed too.`)) return;
        void run(async () => { await api.delete(url); await reload(); });
    };

    return (
        <>
            <tr>
                <td className="mono">
                    {account.email}
                    {account.twoFactorEnabled && <span className="tag tag-muted" style={{ marginLeft: '0.5rem' }}>2FA</span>}
                    {account.forwarding && <div className="hint">Forwards to {account.forwarding}</div>}
                </td>
                <td>{account.usage ?? '—'} / {account.quotaLimit ?? '—'}</td>
                <td><span className={`tag ${account.active ? 'tag-completed' : 'tag-muted'}`}>{account.active ? 'Active' : 'Paused'}</span></td>
                <td className="actions">
                    <button className="btn-outline small" onClick={() => setEditing(v => !v)} disabled={busy}>{editing ? 'Close' : 'Edit'}</button>
                    {account.active
                        ? <button className="btn-outline small" onClick={() => setActive(false)} disabled={busy}>Pause</button>
                        : <button className="btn-outline small" onClick={() => setActive(true)} disabled={busy}>Resume</button>}
                    <button className="btn-outline small danger" onClick={remove} disabled={busy}>Delete</button>
                </td>
            </tr>
            {editing && (
                <tr>
                    <td colSpan={4}>
                        <form className="fi-form" onSubmit={save}>
                            <div className="inline-fields">
                                <label style={{ gridColumn: 'span 2' }}>
                                    New password
                                    <input type="text" className="mono" value={password} onChange={e => setPassword(e.target.value)} minLength={10} autoComplete="new-password" placeholder="Leave empty to keep" />
                                    <span className="hint"><button type="button" className="link-button" onClick={() => setPassword(generatePassword())}>Generate a strong password</button></span>
                                </label>
                                <label>
                                    Quota
                                    <select value={quotaMb} onChange={e => setQuotaMb(Number(e.target.value))}>
                                        {[...new Set([...QUOTAS.map(q => q.mb), quotaMb])].sort((a, b) => a - b).map(mb => (
                                            <option key={mb} value={mb}>{QUOTAS.find(q => q.mb === mb)?.label ?? `${mb} MB`}</option>
                                        ))}
                                    </select>
                                </label>
                            </div>
                            <button className="btn-submit inline" disabled={busy || (!password && quotaMb === account.quotaMb)}>Save changes</button>
                        </form>
                    </td>
                </tr>
            )}
        </>
    );
}

function AliasesCard({ base, domain, aliases, accounts, busy, run, reload }: {
    base: string; domain: string | null; aliases: EmailAlias[]; accounts: EmailAccount[]; busy: boolean;
    run: (fn: () => Promise<void>) => Promise<void>; reload: () => Promise<void>;
}) {
    const [source, setSource] = useState('');
    const [destinations, setDestinations] = useState('');
    const [catchAllTo, setCatchAllTo] = useState('');
    const catchAll = aliases.filter(a => a.catchAll);
    // Group aliases: one source, several destinations.
    const groups = [...aliases.filter(a => !a.catchAll).reduce((m, a) => m.set(a.source, [...(m.get(a.source) ?? []), a]), new Map<string, EmailAlias[]>())];

    const create = (src: string, dest: string[], done: () => void) => {
        void run(async () => {
            await api.post(`${base}/email/aliases`, { source: src, destinations: dest });
            done();
            await reload();
        });
    };
    const remove = (a: EmailAlias) => {
        if (!window.confirm(`Stop forwarding ${a.catchAll ? 'unknown addresses' : a.source} to ${a.destination}?`)) return;
        void run(async () => { await api.delete(`${base}/email/aliases/${encodeURIComponent(a.id)}`); await reload(); });
    };

    return (
        <div className="panel-card">
            <h3>Forwarding &amp; aliases</h3>
            <p className="card-text" style={{ marginBottom: '1rem' }}>
                An alias forwards mail to other addresses without a mailbox of its own. Add several destinations to send to a whole group.
            </p>
            <div className="table-wrap">
                <table className="data-table">
                    <thead><tr><th>Address</th><th>Forwards to</th></tr></thead>
                    <tbody>
                        {groups.map(([src, list]) => (
                            <tr key={src}>
                                <td className="mono">{src}</td>
                                <td>
                                    {list.map(a => (
                                        <div key={a.id} className="mono" style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'center' }}>
                                            {a.destination}
                                            <button className="link-button" onClick={() => remove(a)} disabled={busy}>Remove</button>
                                        </div>
                                    ))}
                                </td>
                            </tr>
                        ))}
                        {groups.length === 0 && <tr><td colSpan={2} className="empty-state">No aliases yet.</td></tr>}
                    </tbody>
                </table>
            </div>

            <form className="fi-form" style={{ marginTop: '1.25rem' }} onSubmit={e => {
                e.preventDefault();
                create(source, destinations.split(/[\s,;]+/).filter(Boolean), () => { setSource(''); setDestinations(''); });
            }}>
                <div className="inline-fields">
                    <label>
                        Address
                        <input value={source} onChange={e => setSource(e.target.value.trim().toLowerCase())} required pattern="[a-z0-9._\-]{1,64}" placeholder="info" />
                        <span className="hint">@{domain}</span>
                    </label>
                    <label style={{ gridColumn: 'span 2' }}>
                        Forward to
                        <input value={destinations} onChange={e => setDestinations(e.target.value)} required placeholder="anna@gmail.com, kalle@foretag.se" />
                        <span className="hint">One or more addresses, separated by commas.</span>
                    </label>
                </div>
                <button className="btn-submit inline" disabled={busy}>Add alias</button>
            </form>

            <h4 style={{ margin: '1.75rem 0 0.5rem', fontSize: '1rem', fontWeight: 600, color: '#fff' }}>Catch-all</h4>
            <p className="card-text" style={{ marginBottom: '0.75rem' }}>
                Receive mail sent to addresses that don't exist on {domain}, e.g. misspelled names.
            </p>
            {catchAll.length > 0 ? catchAll.map(a => (
                <div key={a.id} className="btn-row" style={{ alignItems: 'center' }}>
                    <span>Unknown addresses go to <strong className="mono">{a.destination}</strong></span>
                    <button className="btn-outline small danger" onClick={() => remove(a)} disabled={busy}>Turn off</button>
                </div>
            )) : (
                <form className="fi-form" onSubmit={e => { e.preventDefault(); create('*', [catchAllTo], () => setCatchAllTo('')); }}>
                    <div className="inline-fields">
                        <label style={{ gridColumn: 'span 2' }}>
                            Send unknown addresses to
                            <input value={catchAllTo} onChange={e => setCatchAllTo(e.target.value.trim())} required type="email" list="catch-all-mailboxes" placeholder={accounts[0]?.email ?? `info@${domain}`} />
                            <datalist id="catch-all-mailboxes">{accounts.map(a => <option key={a.email} value={a.email} />)}</datalist>
                        </label>
                        <button className="btn-submit inline" disabled={busy}>Turn on catch-all</button>
                    </div>
                </form>
            )}
        </div>
    );
}

/** Mailboxes, aliases and mail health (docs/CarbonEdge_ForgetIT_Mail_Architecture_and_API_Guide.md). */
export default function EmailTab({ base, detail }: TabProps) {
    const { data, error, loading, reload } = useLoad(
        () => api.get<{ email: EmailInfo; health: MailHealth | null }>(`${base}/email`).then(r => r.data), [base]);
    const [form, setForm] = useState({ username: '', password: '', quotaMb: 3000 });
    const [shown, setShown] = useState<{ email: string; password: string; title: string } | null>(null);
    const action = useAction();
    const email = data?.email;
    const domain = email?.domain ?? detail.service.domain;

    const create = (e: FormEvent) => {
        e.preventDefault();
        void action.run(async () => {
            await api.post(`${base}/email/accounts`, form);
            setShown({ email: `${form.username}@${domain}`, password: form.password, title: `${form.username}@${domain} is ready.` });
            setForm({ username: '', password: '', quotaMb: form.quotaMb });
            await reload();
        });
    };

    if (loading && !data) return <div className="panel-card"><Loading /></div>;

    return (
        <>
            {data?.health && email && email.accounts.length > 0 && <HealthCard health={data.health} domain={domain} linkedDomain={detail.linkedDomain} />}

            <div className="panel-card">
                <div className="panel-card-header">
                    <h3>Mailboxes</h3>
                    {email?.webmailUrl && <a className="btn-outline small" href={email.webmailUrl} target="_blank" rel="noopener noreferrer">Open webmail ↗</a>}
                </div>
                {error && <Alert type="error">{error}</Alert>}
                {action.error && <Alert type="error">{action.error}</Alert>}
                {shown && <PasswordBox {...shown} onDone={() => setShown(null)} />}
                <div className="table-wrap">
                    <table className="data-table">
                        <thead><tr><th>Address</th><th>Used / quota</th><th>Status</th><th /></tr></thead>
                        <tbody>
                            {email?.accounts.map(a => (
                                <MailboxRow key={a.email} account={a} base={base} busy={action.busy} run={action.run} reload={reload}
                                    onPassword={(addr, password) => setShown({ email: addr, password, title: `The password for ${addr} was changed.` })} />
                            ))}
                            {email?.accounts.length === 0 && <tr><td colSpan={4} className="empty-state">No mailboxes yet.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="panel-card">
                <h3>Create mailbox</h3>
                {email?.accounts.length === 0 && (
                    <p className="card-text" style={{ marginBottom: '1rem' }}>The first mailbox also sets up MX, SPF, DKIM and DMARC for {domain}.</p>
                )}
                <form className="fi-form" onSubmit={create}>
                    <div className="inline-fields">
                        <label style={{ gridColumn: 'span 2' }}>
                            Address
                            <input value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value.trim().toLowerCase() }))} required pattern="[a-z0-9._\-]{1,64}" placeholder="kontakt" />
                            <span className="hint">@{domain}</span>
                        </label>
                        <label>
                            Quota
                            <select value={form.quotaMb} onChange={e => setForm(f => ({ ...f, quotaMb: Number(e.target.value) }))}>
                                {QUOTAS.map(q => <option key={q.mb} value={q.mb}>{q.label}</option>)}
                            </select>
                        </label>
                    </div>
                    <label>
                        Password
                        <input type="text" className="mono" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required minLength={10} autoComplete="new-password" />
                        <span className="hint">
                            At least 10 characters.{' '}
                            <button type="button" className="link-button" onClick={() => setForm(f => ({ ...f, password: generatePassword() }))}>Generate a strong password</button>
                        </span>
                    </label>
                    <button className="btn-submit inline" disabled={action.busy}>Create mailbox</button>
                </form>
            </div>

            {email && <AliasesCard base={base} domain={domain} aliases={email.aliases} accounts={email.accounts} busy={action.busy} run={action.run} reload={reload} />}

            {email && (email.serverSettings.imap || email.serverSettings.smtp) && (
                <div className="panel-card">
                    <h3>Mail client settings</h3>
                    <p className="card-text" style={{ marginBottom: '1rem' }}>For iPhone, Android, Outlook and Mac Mail.</p>
                    <div className="kv-grid">
                        {email.serverSettings.imap && (
                            <div><div className="kv-label">Incoming (IMAP)</div><div className="kv-value mono">{email.serverSettings.imap.host} · port {email.serverSettings.imap.port}{email.serverSettings.imap.ssl ? ' · SSL/TLS' : ''}</div></div>
                        )}
                        {email.serverSettings.smtp && (
                            <div><div className="kv-label">Outgoing (SMTP)</div><div className="kv-value mono">{email.serverSettings.smtp.host} · port {email.serverSettings.smtp.port}{email.serverSettings.smtp.ssl ? ' · SSL/TLS' : ''}</div></div>
                        )}
                        <div><div className="kv-label">Username</div><div className="kv-value">The full email address</div></div>
                        <div><div className="kv-label">Password</div><div className="kv-value">The mailbox password (also for sending)</div></div>
                    </div>
                    {email.webmailUrl && (
                        <p className="card-text" style={{ marginTop: '1rem' }}>
                            Out-of-office replies and filters are set in <a className="text-link" href={email.webmailUrl} target="_blank" rel="noopener noreferrer">webmail</a> under Settings → Filters. They run on the server, so your computer doesn't need to be on.
                        </p>
                    )}
                </div>
            )}
        </>
    );
}
